import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  App,
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "../components/icons";
import { DispatchBoardView } from "../components/dispatch/DispatchBoardView";
import { useTenantContext } from "../tenant/TenantContext";
import { trpc } from "../trpc";
import {
  detectSeriesSequenceGaps,
  extractSeriesPrefix,
  formatRoomCodeRanges,
  summarizeRoomCodeMatches,
  type SeriesGapReport,
} from "../../shared/roomCodePattern";
import type { DispatchDetectionReport } from "../../shared/dispatchTypes";

const { Text, Title } = Typography;

const windowOptions = [
  { label: "5 分钟", value: 5 },
  { label: "10 分钟", value: 10 },
  { label: "30 分钟", value: 30 },
];

const roleOptions = [
  { label: "源群", value: "source" },
  { label: "采集群", value: "collect" },
  { label: "目标群", value: "target" },
];

const DISPATCH_MONITOR_UNIT_KEY = "dispatchMonitorBusinessUnitIds";
/** 旧版本单单元选择的存储键，首次读取时做一次性兼容 */
const DISPATCH_MONITOR_LEGACY_UNIT_KEY = "dispatchMonitorBusinessUnitId";
const DISPATCH_MONITOR_SET_KEY = "dispatchMonitorBusinessUnitSetId";
const DISPATCH_MONITOR_POLL_INTERVAL_MS = 10_000;

function readStoredUnitIds(): string[] {
  try {
    const raw =
      localStorage.getItem(DISPATCH_MONITOR_UNIT_KEY) ??
      localStorage.getItem(DISPATCH_MONITOR_LEGACY_UNIT_KEY);
    if (!raw) return [];
    if (raw.startsWith("[")) {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((item): item is string => typeof item === "string" && Boolean(item))
        : [];
    }
    return [raw];
  } catch {
    return [];
  }
}
function sameIds(left: string[], right: string[]) {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

const BUSINESS_UNIT_LIST_SCROLL_Y = "calc(100vh - 320px)";
const BUSINESS_UNIT_ROOM_TABLE_SCROLL_Y = "calc(100vh - 460px)";
const INFER_CANDIDATE_TABLE_SCROLL_Y = "calc(100vh - 360px)";

function formatTime(value: string) {
  if (!value) return "-";
  return new Date(value).toLocaleString("zh-CN");
}

function roleTag(role: string) {
  if (role === "source") return <Tag className="dispatch-role-tag dispatch-role-tag--source">源群</Tag>;
  if (role === "collect") return <Tag className="dispatch-role-tag dispatch-role-tag--collect">采集群</Tag>;
  return <Tag className="dispatch-role-tag dispatch-role-tag--target">目标群</Tag>;
}

function statusTag(status: string) {
  if (status === "success") return <Tag color="success">成功</Tag>;
  if (status === "failed") return <Tag color="error">失败</Tag>;
  return <Tag color="warning">告警</Tag>;
}

type AnyRecord = Record<string, any>;

type GuziInferCandidateRow = {
  wxGroupId: string;
  count: number;
  groupName: string;
};

function Stack({
  children,
  className,
  size = 12,
  style,
}: {
  children: ReactNode;
  className?: string;
  size?: number;
  style?: CSSProperties;
}) {
  return (
    <div
      className={className}
      style={{ display: "flex", flexDirection: "column", gap: size, width: "100%", ...style }}
    >
      {children}
    </div>
  );
}

export function DispatchMonitorPage() {
  const { message, modal } = App.useApp();
  const [summary, setSummary] = useState<AnyRecord | null>(null);
  const [businessUnits, setBusinessUnits] = useState<AnyRecord[]>([]);
  const [businessUnitSets, setBusinessUnitSets] = useState<AnyRecord[]>([]);
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>(readStoredUnitIds);
  const [selectedSetId, setSelectedSetId] = useState<string | undefined>(
    () => localStorage.getItem(DISPATCH_MONITOR_SET_KEY) || undefined
  );
  const restoredSetIdRef = useRef(selectedSetId);
  const [setForm, setSetForm] = useState<{ mode: "create" | "update"; name: string } | null>(null);
  const [setSaving, setSetSaving] = useState(false);
  const [windowMinutes, setWindowMinutes] = useState(5);
  const [detection, setDetection] = useState<DispatchDetectionReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [bootstrapLoaded, setBootstrapLoaded] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string>("");
  const requestIdRef = useRef(0);

  const loadBootstrap = useCallback(async () => {
    const [nextSummary, units, sets] = await Promise.all([
      trpc.fadan.summary.query(),
      trpc.fadan.businessUnits.list.query(),
      trpc.fadan.businessUnitSets.list.query(),
    ]);
    setSummary(nextSummary);
    setBusinessUnits(units);
    setBusinessUnitSets(sets);
    return { units, sets };
  }, []);

  const loadDetection = useCallback(async () => {
    if (selectedUnitIds.length === 0) return;

    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const nextDetection = await trpc.fadan.detection.query({
        businessUnitIds: selectedUnitIds,
        windowMinutes,
      });
      if (requestId !== requestIdRef.current) return;
      setDetection(nextDetection as DispatchDetectionReport);
      setLastRefreshedAt(new Date().toISOString());
    } catch (error: any) {
      if (requestId !== requestIdRef.current) return;
      message.error(error?.message || "加载发单监控数据失败");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [message, selectedUnitIds, windowMinutes]);

  const refreshAll = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const { units } = await loadBootstrap();
      const availableIds = selectedUnitIds.filter((id) =>
        units.some((unit) => unit.id === id)
      );
      const unitIds = availableIds.length > 0 ? availableIds : units[0]?.id ? [units[0].id] : [];
      if (unitIds.length === 0) return;
      // 选择被置空时回退到第一个单元，并同步回选择器，避免界面与实际查询不一致
      setSelectedUnitIds((prev) => (sameIds(prev, unitIds) ? prev : unitIds));
      const nextDetection = await trpc.fadan.detection.query({
        businessUnitIds: unitIds,
        windowMinutes,
      });
      if (requestId !== requestIdRef.current) return;
      setDetection(nextDetection as DispatchDetectionReport);
      setLastRefreshedAt(new Date().toISOString());
    } catch (error: any) {
      if (requestId !== requestIdRef.current) return;
      message.error(error?.message || "加载发单监控数据失败");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [loadBootstrap, message, selectedUnitIds, windowMinutes]);

  useEffect(() => {
    void (async () => {
      try {
        const { units, sets } = await loadBootstrap();
        setBootstrapLoaded(true);
        const restoredSet = restoredSetIdRef.current
          ? sets.find((item: AnyRecord) => item.id === restoredSetIdRef.current)
          : undefined;
        if (restoredSet) {
          setSelectedSetId(restoredSet.id);
          setSelectedUnitIds(restoredSet.businessUnitIds);
          return;
        }
        setSelectedSetId(undefined);
        setSelectedUnitIds((prev) => {
          const kept = prev.filter((id) => units.some((unit) => unit.id === id));
          if (kept.length === prev.length) return prev;
          return kept.length > 0 ? kept : units[0]?.id ? [units[0].id] : [];
        });
      } catch (error: any) {
        setBootstrapLoaded(true);
        message.error(error?.message || "加载发单监控基础数据失败");
      }
    })();
  }, [loadBootstrap, message]);

  useEffect(() => {
    if (selectedUnitIds.length > 0) {
      localStorage.setItem(DISPATCH_MONITOR_UNIT_KEY, JSON.stringify(selectedUnitIds));
      localStorage.removeItem(DISPATCH_MONITOR_LEGACY_UNIT_KEY);
    }
  }, [selectedUnitIds]);

  useEffect(() => {
    if (selectedSetId) localStorage.setItem(DISPATCH_MONITOR_SET_KEY, selectedSetId);
    else localStorage.removeItem(DISPATCH_MONITOR_SET_KEY);
  }, [selectedSetId]);

  const selectedSet = businessUnitSets.find((item) => item.id === selectedSetId);

  const applyUnitSet = useCallback(
    (setId?: string) => {
      setSelectedSetId(setId);
      if (!setId) return;
      const target = businessUnitSets.find((item) => item.id === setId);
      setSelectedUnitIds(target?.businessUnitIds || []);
    },
    [businessUnitSets]
  );

  const handleUnitsChange = useCallback((unitIds: string[]) => {
    setSelectedUnitIds(unitIds);
    // 手动改成员后与已保存的集合不再一致，避免误以为还在看那个集合
    setSelectedSetId(undefined);
  }, []);

  const submitSetForm = useCallback(async () => {
    if (!setForm) return;
    const name = setForm.name.trim();
    if (!name) {
      message.warning("请输入业务单元集合名称");
      return;
    }
    if (selectedUnitIds.length === 0) {
      message.warning("请先选择要聚合的业务单元");
      return;
    }
    setSetSaving(true);
    try {
      if (setForm.mode === "create") {
        const created = await trpc.fadan.businessUnitSets.create.mutate({
          name,
          businessUnitIds: selectedUnitIds,
        });
        setSelectedSetId(created.id);
        message.success(`已保存集合「${created.name}」`);
      } else {
        if (!selectedSetId) return;
        await trpc.fadan.businessUnitSets.update.mutate({
          id: selectedSetId,
          data: { name, businessUnitIds: selectedUnitIds },
        });
        message.success(`已更新集合「${name}」`);
      }
      setSetForm(null);
      await loadBootstrap();
    } catch (error: any) {
      message.error(error?.message || "保存集合失败");
    } finally {
      setSetSaving(false);
    }
  }, [loadBootstrap, message, selectedSetId, selectedUnitIds, setForm]);

  const removeSelectedSet = useCallback(() => {
    if (!selectedSet) return;
    modal.confirm({
      title: `删除业务单元集合「${selectedSet.name}」`,
      content: "仅删除该集合，不会影响业务单元本身。",
      okText: "删除",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: async () => {
        await trpc.fadan.businessUnitSets.remove.mutate({ id: selectedSet.id });
        setSelectedSetId(undefined);
        await loadBootstrap();
        message.success("集合已删除");
      },
    });
  }, [loadBootstrap, message, modal, selectedSet]);

  useEffect(() => {
    if (selectedUnitIds.length === 0) return;

    let cancelled = false;
    let pollTimer: number | undefined;

    const scheduleNextPoll = () => {
      if (cancelled || windowMinutes > 60) return;
      pollTimer = window.setTimeout(() => {
        void runPollCycle();
      }, DISPATCH_MONITOR_POLL_INTERVAL_MS);
    };

    const runPollCycle = async () => {
      if (cancelled) return;
      await loadDetection();
      scheduleNextPoll();
    };

    void runPollCycle();

    return () => {
      cancelled = true;
      requestIdRef.current += 1;
      if (pollTimer) window.clearTimeout(pollTimer);
    };
  }, [loadDetection, windowMinutes, selectedUnitIds]);

  return (
    <Stack size={20} className="fade-in">
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>发单监控看板</Title>
          <Text type="secondary">按业务单元聚合源群、采集群、目标群，检测相同文案和媒体的扩散链路。可多选业务单元聚合查看。</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={refreshAll} loading={loading}>刷新</Button>
      </div>

      <Spin spinning={loading}>
        {bootstrapLoaded && businessUnits.length === 0 ? (
          <Card variant="borderless" className="shadow-sm">
            <Empty
              description="暂无已授权的业务单元，请联系管理员授权后查看发单监控数据。"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        ) : (
        <Stack size={20}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} xl={6}>
              <Card variant="borderless" className="shadow-sm">
                <Statistic title="启用存档源" value={summary?.sourceCount || 0} prefix={<KeyOutlined />} />
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Card variant="borderless" className="shadow-sm">
                <Statistic title="24 小时消息" value={summary?.messageCount || 0} prefix={<FileTextOutlined />} />
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Card variant="borderless" className="shadow-sm">
                <Statistic title="活跃群聊" value={summary?.roomCount || 0} prefix={<TeamOutlined />} />
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Card variant="borderless" className="shadow-sm">
                <Statistic title="检测结果" value={detection?.summary?.matched || 0} prefix={<ThunderboltOutlined />} />
              </Card>
            </Col>
          </Row>

          <Card variant="borderless" className="shadow-sm">
            <div className="monitor-toolbar">
              <Select
                value={selectedSetId}
                style={{ minWidth: 180 }}
                placeholder="业务单元集合"
                allowClear
                options={businessUnitSets.map((item) => ({ label: item.name, value: item.id }))}
                onChange={(value) => applyUnitSet(value)}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: "4px 0" }} />
                    <Space size={4} style={{ padding: "0 8px 6px" }} wrap>
                      <Button
                        type="text"
                        size="small"
                        icon={<PlusOutlined />}
                        disabled={selectedUnitIds.length === 0}
                        onClick={() => setSetForm({ mode: "create", name: "" })}
                      >
                        存为新集合
                      </Button>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        disabled={!selectedSetId || selectedUnitIds.length === 0}
                        onClick={() =>
                          setSetForm({ mode: "update", name: String(selectedSet?.name || "") })
                        }
                      >
                        用当前选择更新
                      </Button>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={!selectedSetId}
                        onClick={removeSelectedSet}
                      >
                        删除集合
                      </Button>
                    </Space>
                  </>
                )}
              />
              <Select
                mode="multiple"
                value={selectedUnitIds}
                style={{ minWidth: 280, maxWidth: 460 }}
                placeholder="业务单元（可多选聚合查看）"
                maxTagCount="responsive"
                allowClear
                options={businessUnits.map((item) => ({ label: item.name, value: item.id }))}
                onChange={handleUnitsChange}
              />
              <Segmented
                value={windowMinutes}
                options={windowOptions}
                onChange={(value) => setWindowMinutes(Number(value))}
              />
              <Space size={8} wrap className="dispatch-board-stats">
                <Tag className="dispatch-role-tag dispatch-role-tag--source">源头 {detection?.summary?.source || 0}</Tag>
                <Tag className="dispatch-role-tag dispatch-role-tag--collect">采集 {detection?.summary?.collect || 0}</Tag>
                <Tag className="dispatch-role-tag dispatch-role-tag--target">发单 {detection?.summary?.target || 0}</Tag>
              </Space>
              <Text type="secondary" className="dispatch-board-refresh">
                {lastRefreshedAt ? `上次刷新 ${formatTime(lastRefreshedAt)}` : ""}
              </Text>
            </div>
          </Card>

          <Card variant="borderless" className="shadow-sm dispatch-board-card">
            {detection && selectedUnitIds.length > 0 ? (
              <DispatchBoardView report={detection} />
            ) : (
              <Empty
                description="请选择至少一个业务单元，或在上方选择一个业务单元集合；选多个时可在一个看板内聚合查看。"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>

          {selectedSet && Number(selectedSet.hiddenUnitCount || 0) > 0 ? (
            <Alert
              type="info"
              showIcon
              message={`集合「${selectedSet.name}」中有 ${selectedSet.hiddenUnitCount} 个业务单元未授权给你，当前仅聚合计权范围。`}
            />
          ) : null}

        </Stack>
        )}
      </Spin>

      <Modal
        open={!!setForm}
        title={setForm?.mode === "create" ? "存为业务单元集合" : "更新业务单元集合"}
        okText="保存"
        cancelText="取消"
        confirmLoading={setSaving}
        onOk={submitSetForm}
        onCancel={() => setSetForm(null)}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Input
            value={setForm?.name || ""}
            maxLength={80}
            placeholder="集合名称，例如：华东大区"
            onChange={(event) =>
              setSetForm((prev) => (prev ? { ...prev, name: event.target.value } : prev))
            }
          />
          <Text type="secondary">
            包含 {selectedUnitIds.length} 个业务单元：
            {selectedUnitIds
              .map(
                (id) => businessUnits.find((unit) => unit.id === id)?.name || id
              )
              .join("、") || "-"}
          </Text>
        </Space>
      </Modal>
    </Stack>
  );
}

export function ArchiveSourceSettingsPage() {
  const { message, modal } = App.useApp();
  const [form] = Form.useForm();
  const [sources, setSources] = useState<AnyRecord[]>([]);
  const [logs, setLogs] = useState<AnyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AnyRecord | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [nextSources, nextLogs] = await Promise.all([
        trpc.fadan.sources.list.query(),
        trpc.fadan.logs.query({ limit: 40 }),
      ]);
      setSources(nextSources);
      setLogs(nextLogs);
    } catch (error: any) {
      message.error(error?.message || "加载存档源失败");
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      name: "",
      baseUrl: "",
      tenantId: "",
      corpId: "",
      apiKey: "",
      enabled: true,
      profileRefreshEnabled: true,
    });
    setModalOpen(true);
  };

  const openEdit = (record: AnyRecord) => {
    setEditing(record);
    form.setFieldsValue({ ...record, apiKey: "" });
    setModalOpen(true);
  };

  const handleSave = async (values: AnyRecord) => {
    setSaving(true);
    try {
      if (editing) {
        await trpc.fadan.sources.update.mutate({ id: editing.id, data: values });
        message.success("存档源已更新");
      } else {
        await trpc.fadan.sources.create.mutate({
          name: values.name,
          baseUrl: values.baseUrl,
          tenantId: values.tenantId,
          corpId: values.corpId,
          apiKey: values.apiKey,
          enabled: values.enabled,
          profileRefreshEnabled: values.profileRefreshEnabled,
        });
        message.success("存档源已创建");
      }
      setModalOpen(false);
      loadData();
    } catch (error: any) {
      message.error(error?.message || "保存存档源失败");
    } finally {
      setSaving(false);
    }
  };

  const sourceColumns: ColumnsType<AnyRecord> = [
    {
      title: "存档源",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <Stack size={2}>
          <Space wrap>
            <Text strong>{record.name}</Text>
            <Tag color={record.enabled ? "success" : "default"}>{record.enabled ? "启用" : "禁用"}</Tag>
            <Tag color={record.profileRefreshEnabled ? "processing" : "default"}>
              {record.profileRefreshEnabled ? "群画像开启" : "群画像关闭"}
            </Tag>
          </Space>
          <Text type="secondary">{record.baseUrl}</Text>
        </Stack>
      ),
    },
    {
      title: "租户",
      key: "tenant",
      width: 220,
      render: (_, record) => (
        <Stack size={0}>
          <Text copyable>{record.tenantId}</Text>
          <Text type="secondary">{record.corpId}</Text>
        </Stack>
      ),
    },
    {
      title: "API Key",
      dataIndex: "apiKeyMasked",
      key: "apiKeyMasked",
      width: 150,
    },
    {
      title: "回调地址",
      dataIndex: "callbackPath",
      key: "callbackPath",
      width: 280,
      render: (value) => <Text copyable={{ text: value }}>{value}</Text>,
    },
    {
      title: "最近同步",
      dataIndex: "lastSyncAt",
      key: "lastSyncAt",
      width: 180,
      render: formatTime,
    },
    {
      title: "操作",
      key: "actions",
      width: 210,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="测试 API Key">
            <Button
              type="text"
              size="small"
              icon={<ThunderboltOutlined />}
              onClick={async () => {
                try {
                  await trpc.fadan.sources.test.mutate({ id: record.id });
                  message.success("连通性测试成功");
                  loadData();
                } catch (error: any) {
                  message.error(error?.message || "连通性测试失败");
                }
              }}
            />
          </Tooltip>
          <Tooltip title="立即补偿拉取">
            <Button
              type="text"
              size="small"
              icon={<SyncOutlined />}
              onClick={async () => {
                try {
                  const result = await trpc.fadan.sources.sync.mutate({ id: record.id });
                  message.success(`补偿拉取完成，处理 ${result.pulled} 条`);
                  loadData();
                } catch (error: any) {
                  message.error(error?.message || "补偿拉取失败");
                }
              }}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="删除存档源"
            description="会同步删除该源的消息、画像和日志。"
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            onConfirm={() => {
              trpc.fadan.sources.remove.mutate({ id: record.id }).then(() => {
                message.success("存档源已删除");
                loadData();
              }).catch((error: any) => message.error(error?.message || "删除失败"));
            }}
          >
            <Tooltip title="删除">
              <Button type="text" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const logColumns: ColumnsType<AnyRecord> = [
    { title: "时间", dataIndex: "createdAt", key: "createdAt", width: 180, render: formatTime },
    { title: "类型", dataIndex: "type", key: "type", width: 100 },
    { title: "状态", dataIndex: "status", key: "status", width: 100, render: statusTag },
    {
      title: "存档源",
      dataIndex: "sourceName",
      key: "sourceName",
      width: 220,
      render: (value: string, record) => value || record.tenantId || "-",
    },
    {
      title: "群",
      key: "chatId",
      width: 200,
      render: (_, record) => {
        const chatId = String(record.meta?.chatId || "").trim();
        return chatId ? <Text copyable>{chatId}</Text> : "-";
      },
    },
    { title: "信息", dataIndex: "message", key: "message" },
  ];

  return (
    <Stack size={20} className="fade-in">
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>会话存档源配置</Title>
          <Text type="secondary">配置 API Key、租户授权和实时推送入口，支撑消息补偿拉取与群成员画像刷新。</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增存档源</Button>
        </Space>
      </div>

      <Card variant="borderless" className="shadow-sm">
        <Table rowKey="id" columns={sourceColumns} dataSource={sources} loading={loading} scroll={{ x: 1180 }} />
      </Card>

      <Card title="同步与调用日志" variant="borderless" className="shadow-sm">
        <Table rowKey="id" columns={logColumns} dataSource={logs} pagination={false} size="small" />
      </Card>

      <Modal
        title={editing ? "编辑存档源" : "新增存档源"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        width={720}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="name" label="名称" rules={[{ required: true, message: "请输入名称" }]}>
                <Input placeholder="主会话存档" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="enabled" label="状态">
                <Select options={[{ label: "启用", value: true }, { label: "禁用", value: false }]} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="baseUrl" label="会话存档地址" rules={[{ required: true, message: "请输入地址" }]}>
            <Input placeholder="https://archive.example.com" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="tenantId" label="Tenant ID" rules={[{ required: true, message: "请输入 tenantId" }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="corpId" label="Corp ID" rules={[{ required: true, message: "请输入 corpId" }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="profileRefreshEnabled"
            label="群画像刷新"
            valuePropName="checked"
            extra="只有会话存档服务已配置企微内部应用 Secret 时才开启。关闭后仍正常接收和存档消息。"
          >
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
          <Form.Item
            name="apiKey"
            label="API Key"
            rules={editing ? [] : [{ required: true, message: "请输入 API Key" }]}
            extra={editing ? "留空则不更换 API Key。" : "明文只用于本次保存，服务端会加密存储。"}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Stack>
  );
}

export function BusinessUnitManagementPage() {
  const { message } = App.useApp();
  const [unitForm] = Form.useForm();
  const [groupForm] = Form.useForm();
  const [splitGroupForm] = Form.useForm();
  const [roomForm] = Form.useForm();
  const [batchRoomForm] = Form.useForm();
  const [batchRoleForm] = Form.useForm();
  const [batchGroupForm] = Form.useForm();
  const [units, setUnits] = useState<AnyRecord[]>([]);
  const [rooms, setRooms] = useState<AnyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState<AnyRecord | null>(null);
  const [deleteGroupRooms, setDeleteGroupRooms] = useState(false);
  const [deletingGroupLoading, setDeletingGroupLoading] = useState(false);
  const [splittingGroup, setSplittingGroup] = useState<AnyRecord | null>(null);
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [batchRoomModalOpen, setBatchRoomModalOpen] = useState(false);
  const [batchRoleModalOpen, setBatchRoleModalOpen] = useState(false);
  const [batchGroupModalOpen, setBatchGroupModalOpen] = useState(false);
  const [roleFilters, setRoleFilters] = useState<string[]>([]);
  const [groupFilters, setGroupFilters] = useState<string[]>([]);
  const [roomNameKeyword, setRoomNameKeyword] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [seriesGapReport, setSeriesGapReport] = useState<SeriesGapReport | null>(null);
  const batchPattern = Form.useWatch("pattern", batchRoomForm) || "";
  const batchNickname = Form.useWatch("nickname", batchRoomForm) || "";
  // 订阅系列推送配置，保证「删除」标记后行能够立即隐藏（仅隐藏、不卸载，避免表单值出现空洞）
  Form.useWatch("realUserSeriesPushes", unitForm);
  const [editingUnit, setEditingUnit] = useState<AnyRecord | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [nextUnits, nextRooms] = await Promise.all([
        trpc.fadan.businessUnits.list.query(),
        trpc.fadan.businessUnits.availableRooms.query(),
      ]);
      setUnits(nextUnits);
      setRooms(nextRooms);
      setSelectedUnitId((current) => current || nextUnits[0]?.id);
    } catch (error: any) {
      message.error(error?.message || "加载业务单元失败");
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSelectedRowKeys([]);
    setRoleFilters([]);
    setGroupFilters([]);
    setRoomNameKeyword("");
  }, [selectedUnitId]);

  const selectedUnit = units.find((item) => item.id === selectedUnitId);

  const groupOptions = (selectedUnit?.groups || []).map((group: AnyRecord) => ({ label: group.name, value: group.id }));
  const roomOptions = rooms.map((room) => ({
    value: room.roomId,
    label: room.roomName || room.roomId,
  }));
  const tableRows = useMemo(() => {
    return (selectedUnit?.groups || [])
      .flatMap((group: AnyRecord) =>
        (group.rooms || []).map((room: AnyRecord) => ({ ...room, groupId: group.id, groupName: group.name }))
      )
      .sort((a: AnyRecord, b: AnyRecord) =>
        String(a.roomName || a.roomId || "").localeCompare(
          String(b.roomName || b.roomId || ""),
          "zh-CN"
        )
      );
  }, [selectedUnit]);
  const filteredTableRows = useMemo(() => {
    const keyword = roomNameKeyword.trim().toLowerCase();
    return tableRows.filter((row: AnyRecord) => {
      if (roleFilters.length > 0 && !roleFilters.includes(row.role)) return false;
      if (groupFilters.length > 0 && !groupFilters.includes(row.groupId)) return false;
      if (!keyword) return true;
      const roomName = String(row.roomName || "").toLowerCase();
      return roomName.includes(keyword);
    });
  }, [tableRows, roleFilters, groupFilters, roomNameKeyword]);
  const seriesStats = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of tableRows) {
      const letter = extractSeriesPrefix(String(row.roomName || row.roomId || ""));
      if (!letter) continue;
      counts.set(letter, (counts.get(letter) || 0) + 1);
    }
    return Array.from(counts.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [tableRows]);
  const roomTargets = useMemo(
    () =>
      tableRows.map((row: AnyRecord) => ({
        roomId: String(row.roomId || ""),
        roomName: String(row.roomName || row.roomId || ""),
      })),
    [tableRows]
  );
  const seriesGapByLetter = useMemo(() => {
    const map = new Map<string, SeriesGapReport | null>();
    for (const [letter] of seriesStats) {
      map.set(letter, detectSeriesSequenceGaps(roomTargets, letter));
    }
    return map;
  }, [seriesStats, roomTargets]);

  const openSeriesGapCheck = useCallback(
    (letter: string) => {
      const report = detectSeriesSequenceGaps(roomTargets, letter);
      if (!report) {
        message.info(`${letter}系列暂无群`);
        return;
      }
      setSeriesGapReport(report);
    },
    [message, roomTargets]
  );

  const batchPreview = useMemo(() => {
    if (!batchPattern.trim()) {
      return {
        matched: [] as AnyRecord[],
        unmatchedCodes: [] as string[],
        unmatchedCodeRanges: [] as string[],
        codes: [] as string[],
        error: "",
      };
    }
    try {
      const summary = summarizeRoomCodeMatches(
        rooms.map((room) => ({
          roomId: String(room.roomId || ""),
          roomName: String(room.roomName || room.roomId || ""),
        })),
        batchPattern,
        batchNickname
      );
      return {
        ...summary,
        unmatchedCodeRanges: formatRoomCodeRanges(summary.unmatchedCodes),
        error: "",
      };
    } catch (error: any) {
      return {
        matched: [] as AnyRecord[],
        unmatchedCodes: [] as string[],
        unmatchedCodeRanges: [] as string[],
        codes: [] as string[],
        error: error?.message || "匹配规则无效",
      };
    }
  }, [batchPattern, batchNickname, rooms]);

  const roomColumns: ColumnsType<AnyRecord> = [
    { title: "群聊", dataIndex: "roomName", key: "roomName", render: (_, record) => <Text copyable={{ text: record.roomId }}>{record.roomName || record.roomId}</Text> },
    { title: "分组", dataIndex: "groupName", key: "groupName", width: 160 },
    { title: "角色", dataIndex: "role", key: "role", width: 120, render: roleTag },
    {
      title: "操作",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="移除群角色"
          okText="移除"
          cancelText="取消"
          onConfirm={() => {
            trpc.fadan.businessUnits.removeRoom.mutate({ id: record.id }).then(() => {
              message.success("已移除群角色");
              loadData();
            }).catch((error: any) => message.error(error?.message || "移除失败"));
          }}
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Stack size={20} className="fade-in">
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>业务单元与群角色</Title>
          <Text type="secondary">维护业务单元、分组，以及源群、采集群、目标群角色关系。</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新</Button>
          <Button icon={<PlusOutlined />} onClick={() => {
            groupForm.setFieldsValue({ businessUnitId: selectedUnitId, name: "", description: "", sort: 0 });
            setGroupModalOpen(true);
          }} disabled={!selectedUnitId}>新增分组</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setEditingUnit(null);
            unitForm.setFieldsValue({
              name: "",
              churnWebhookUrl: "",
              clearChurnWebhook: false,
              hasChurnWebhook: false,
              description: "",
              enabled: true,
              realUserSeriesPushes: [],
            });
            setUnitModalOpen(true);
          }}>新增业务单元</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={7}>
          <Card
            title="业务单元"
            variant="borderless"
            className="shadow-sm"
            extra={
              <Popconfirm
                title="删除业务单元"
                description="将删除该业务单元及其所有分组和群角色，且不可恢复。"
                okText="删除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
                onConfirm={() => {
                  if (!selectedUnitId) return;
                  trpc.fadan.businessUnits.remove.mutate({ id: selectedUnitId }).then(() => {
                    message.success("业务单元已删除");
                    setSelectedUnitId(undefined);
                    loadData();
                  }).catch((error: any) => message.error(error?.message || "删除失败"));
                }}
              >
                <Button danger size="small" disabled={!selectedUnitId} icon={<DeleteOutlined />}>
                  删除业务单元
                </Button>
              </Popconfirm>
            }
          >
            <div className="business-unit-list-scroll" style={{ maxHeight: BUSINESS_UNIT_LIST_SCROLL_Y }}>
            <Stack>
              {units.map((unit) => (
                <div
                  key={unit.id}
                  className={`unit-list-item ${unit.id === selectedUnitId ? "active" : ""}`}
                  onClick={() => setSelectedUnitId(unit.id)}
                >
                  <div>
                    <Text strong>{unit.name}</Text>
                    <div><Text type="secondary">{unit.description || "无描述"}</Text></div>
                  </div>
                  <Space>
                    <Tag>{Number(unit.roomCount) || 0} 群</Tag>
                    <Tag color={unit.enabled ? "success" : "default"}>{unit.enabled ? "启用" : "禁用"}</Tag>
                    <Tag
                      color={
                        unit.realUserSeriesPushes?.some((item: AnyRecord) => item.enabled)
                          ? "processing"
                          : "default"
                      }
                    >
                      {unit.realUserSeriesPushes?.filter((item: AnyRecord) => item.enabled).length || 0}
                      个系列推送
                    </Tag>
                    <Tag color={unit.hasChurnWebhook ? "processing" : "default"}>流失通知</Tag>
                    <Tooltip title="编辑业务单元">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          setEditingUnit(unit);
                          unitForm.setFieldsValue({
                            ...unit,
                            churnWebhookUrl: "",
                            clearChurnWebhook: false,
                            realUserSeriesPushes: (unit.realUserSeriesPushes || []).map(
                              (item: AnyRecord) => ({
                                ...item,
                                webhooks:
                                  item.webhookCount > 0
                                    ? Array.from({ length: item.webhookCount }, (_, index) => ({
                                        url: "",
                                        remove: false,
                                        existingIndex: index,
                                      }))
                                    : [{ url: "", remove: false }],
                                clearWebhooks: false,
                              })
                            ),
                          });
                          setUnitModalOpen(true);
                        }}
                      />
                    </Tooltip>
                  </Space>
                </div>
              ))}
              {units.length === 0 && <Text type="secondary">暂无业务单元</Text>}
            </Stack>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={17}>
          <Card
            title={selectedUnit ? `${selectedUnit.name} 的群角色` : "群角色"}
            variant="borderless"
            className="shadow-sm"
            extra={
              <Space>
                <Popconfirm
                  title="清空群角色"
                  description={selectedUnit ? `将移除「${selectedUnit.name}」下的全部群角色，分组将保留。` : "将移除当前业务单元下的全部群角色。"}
                  okText="清空"
                  cancelText="取消"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => {
                    if (!selectedUnitId) return;
                    trpc.fadan.businessUnits.clearRooms.mutate({ businessUnitId: selectedUnitId }).then((result) => {
                      message.success(`已清空 ${result.deletedCount} 个群角色`);
                      loadData();
                    }).catch((error: any) => message.error(error?.message || "清空失败"));
                  }}
                >
                  <Button danger disabled={!selectedUnitId || tableRows.length === 0} icon={<DeleteOutlined />}>
                    清空
                  </Button>
                </Popconfirm>
                <Button
                  icon={<ThunderboltOutlined />}
                  disabled={!selectedUnitId || groupOptions.length === 0}
                  onClick={() => {
                    batchRoomForm.setFieldsValue({
                      businessUnitId: selectedUnitId,
                      groupId: groupOptions[0]?.value,
                      role: "source",
                      pattern: "",
                      nickname: "",
                    });
                    setBatchRoomModalOpen(true);
                  }}
                >
                  批量分配
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={!selectedUnitId || groupOptions.length === 0}
                  onClick={() => {
                    roomForm.setFieldsValue({
                      businessUnitId: selectedUnitId,
                      groupId: groupOptions[0]?.value,
                      role: "source",
                      roomIds: [],
                    });
                    setRoomModalOpen(true);
                  }}
                >
                  分配群角色
                </Button>
              </Space>
            }
          >
            <Stack size={12} style={{ marginBottom: 16 }}>
              {selectedUnitId && (
                <div style={{ width: "100%" }}>
                  <Space wrap>
                    <Tag>总群数 {tableRows.length}</Tag>
                    {seriesStats.map(([letter, count]) => {
                      const gapReport = seriesGapByLetter.get(letter);
                      const isComplete = gapReport?.isComplete ?? false;
                      return (
                        <Tooltip
                          key={letter}
                          title={isComplete ? "序号连续完整，点击查看详情" : "序号存在缺失，点击检测"}
                        >
                          <Tag
                            color={isComplete ? "blue" : "orange"}
                            style={{ cursor: "pointer" }}
                            onClick={() => openSeriesGapCheck(letter)}
                          >
                            {letter}系列 {count}
                          </Tag>
                        </Tooltip>
                      );
                    })}
                  </Space>
                </div>
              )}
              {selectedUnitId && groupOptions.length > 0 && (
                <div style={{ width: "100%" }}>
                  <Space wrap>
                    <Text type="secondary">分组</Text>
                    {(selectedUnit?.groups || []).map((group: AnyRecord) => (
                      <Space key={group.id} size={2}>
                        <Tag>{group.name} {group.rooms?.length || 0}</Tag>
                        <Tooltip title={`拆分「${group.name}」为业务单元`}>
                          <Button
                            type="text"
                            size="small"
                            icon={<TeamOutlined />}
                            onClick={() => {
                              splitGroupForm.setFieldsValue({
                                groupId: group.id,
                                name: group.name,
                                description: group.description || "",
                              });
                              setSplittingGroup(group);
                            }}
                          />
                        </Tooltip>
                        <Tooltip title={`删除分组「${group.name}」`}>
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              setDeletingGroup(group);
                              setDeleteGroupRooms(false);
                            }}
                          />
                        </Tooltip>
                      </Space>
                    ))}
                  </Space>
                </div>
              )}
              <div style={{ width: "100%" }}>
                <Space wrap>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="筛选角色"
                    style={{ minWidth: 220 }}
                    options={roleOptions}
                    value={roleFilters}
                    onChange={(values) => {
                      setRoleFilters(values);
                      setSelectedRowKeys([]);
                    }}
                    maxTagCount="responsive"
                  />
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="筛选分组"
                    style={{ minWidth: 220 }}
                    options={groupOptions}
                    value={groupFilters}
                    onChange={(values) => {
                      setGroupFilters(values);
                      setSelectedRowKeys([]);
                    }}
                    maxTagCount="responsive"
                  />
                  <Input
                    allowClear
                    placeholder="搜索群名"
                    style={{ width: 220 }}
                    value={roomNameKeyword}
                    onChange={(event) => {
                      setRoomNameKeyword(event.target.value);
                      setSelectedRowKeys([]);
                    }}
                  />
                  {selectedRowKeys.length > 0 && (
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => {
                        batchRoleForm.setFieldsValue({ role: undefined });
                        setBatchRoleModalOpen(true);
                      }}
                    >
                      批量改角色（{selectedRowKeys.length}）
                    </Button>
                  )}
                  {selectedRowKeys.length > 0 && (
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => {
                        batchGroupForm.setFieldsValue({ groupId: undefined, newGroupName: "" });
                        setBatchGroupModalOpen(true);
                      }}
                    >
                      批量设置分组（{selectedRowKeys.length}）
                    </Button>
                  )}
                  {selectedRowKeys.length > 0 && (
                    <Popconfirm
                      title={`删除选中的 ${selectedRowKeys.length} 个群角色`}
                      description="删除后不可恢复，确认继续吗？"
                      okText="删除"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                      onConfirm={async () => {
                        try {
                          const result = await trpc.fadan.businessUnits.batchRemoveRooms.mutate({ ids: selectedRowKeys });
                          message.success(`已删除 ${result.deletedCount} 个群角色`);
                          setSelectedRowKeys([]);
                          loadData();
                        } catch (error: any) {
                          message.error(error?.message || "批量删除失败");
                        }
                      }}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        批量删除（{selectedRowKeys.length}）
                      </Button>
                    </Popconfirm>
                  )}
                  {(roleFilters.length > 0 || groupFilters.length > 0 || roomNameKeyword.trim()) && (
                    <Text type="secondary">
                      显示 {filteredTableRows.length} / {tableRows.length} 条
                    </Text>
                  )}
                </Space>
              </div>
            </Stack>
            <Table
              rowKey="id"
              columns={roomColumns}
              dataSource={filteredTableRows}
              loading={loading}
              scroll={{ y: BUSINESS_UNIT_ROOM_TABLE_SCROLL_Y }}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys as string[]),
              }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title={splittingGroup ? `拆分分组「${splittingGroup.name}」` : "拆分分组"}
        open={!!splittingGroup}
        onCancel={() => setSplittingGroup(null)}
        onOk={() => splitGroupForm.submit()}
        okText="拆分为业务单元"
        cancelText="取消"
      >
        <Form
          form={splitGroupForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              const result = await trpc.fadan.businessUnits.splitGroup.mutate(values);
              message.success(`已拆分为业务单元「${result.name}」，迁移 ${result.movedRoomCount} 个群角色`);
              setSplittingGroup(null);
              setSelectedRowKeys([]);
              await loadData();
              setSelectedUnitId(result.id);
            } catch (error: any) {
              message.error(error?.message || "拆分业务单元失败");
            }
          }}
        >
          <Form.Item name="groupId" hidden><Input /></Form.Item>
          <Alert
            type="info"
            showIcon
            message="该分组及其群角色会整体迁移到新业务单元，原业务单元中的其他分组不受影响。"
            style={{ marginBottom: 16 }}
          />
          <Form.Item name="name" label="新业务单元名称" rules={[{ required: true, message: "请输入业务单元名称" }]}>
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={deletingGroup ? `删除分组「${deletingGroup.name}」` : "删除分组"}
        open={!!deletingGroup}
        onCancel={() => setDeletingGroup(null)}
        onOk={async () => {
          if (!deletingGroup) return;
          setDeletingGroupLoading(true);
          try {
            const result = await trpc.fadan.businessUnits.removeGroup.mutate({
              id: deletingGroup.id,
              deleteRooms: deleteGroupRooms,
            });
            if (deleteGroupRooms) {
              message.success(`分组已删除，并删除 ${result.deletedRoomCount} 个群角色`);
            } else if (result.movedRoomCount === 0) {
              message.success("空分组已删除");
            } else {
              message.success(`分组已删除，${result.movedRoomCount} 个群角色已移至「${result.targetGroupName}」`);
            }
            setDeletingGroup(null);
            setSelectedRowKeys([]);
            loadData();
          } catch (error: any) {
            message.error(error?.message || "删除分组失败");
          } finally {
            setDeletingGroupLoading(false);
          }
        }}
        okText="确认删除"
        cancelText="取消"
        confirmLoading={deletingGroupLoading}
        okButtonProps={{ danger: true }}
      >
        <Stack size={16}>
          <Text type="secondary">
            该分组包含 {deletingGroup?.rooms?.length || 0} 个群角色，请选择删除方式。
          </Text>
          <Radio.Group value={deleteGroupRooms} onChange={(event) => setDeleteGroupRooms(event.target.value)}>
            <Stack>
              <Radio value={false}>仅删除分组，群角色移至系统兜底分组</Radio>
              <Radio value={true}>删除分组，并连带删除分组内全部群角色</Radio>
            </Stack>
          </Radio.Group>
        </Stack>
      </Modal>

      <Modal
        title={editingUnit ? "编辑业务单元" : "新增业务单元"}
        open={unitModalOpen}
        onCancel={() => setUnitModalOpen(false)}
        onOk={() => unitForm.submit()}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={unitForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              if (editingUnit) {
                await trpc.fadan.businessUnits.update.mutate({ id: editingUnit.id, data: values });
                message.success("业务单元已更新");
              } else {
                await trpc.fadan.businessUnits.create.mutate(values);
                message.success("业务单元已创建");
              }
              setUnitModalOpen(false);
              loadData();
            } catch (error: any) {
              message.error(error?.message || "保存失败");
            }
          }}
        >
          <Form.Item name="name" label="名称" rules={[{ required: true, message: "请输入名称" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="enabled" label="状态">
            <Select options={[{ label: "启用", value: true }, { label: "禁用", value: false }]} />
          </Form.Item>
          <Form.Item label="字母系列飞书推送">
            <Form.List name="realUserSeriesPushes">
              {(fields) => (
                <Stack>
                  {fields.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="该业务单元暂未识别到字母系列群"
                    />
                  ) : (
                    fields.map((field) => (
                      <div
                        key={field.key}
                        style={{
                          border: "1px solid var(--ant-color-border-secondary)",
                          padding: 12,
                          borderRadius: 6,
                        }}
                      >
                        <Form.Item name={[field.name, "letter"]} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "hasWebhook"]}
                          hidden
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                        <Row gutter={12} align="middle">
                          <Col xs={24} md={5}>
                            <Form.Item label="字母系列">
                              <Tag color="blue" style={{ fontSize: 16, padding: "3px 12px" }}>
                                {unitForm.getFieldValue([
                                  "realUserSeriesPushes",
                                  field.name,
                                  "letter",
                                ])} 系列
                              </Tag>
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={14}>
                            <Form.Item label="飞书 Webhook">
                              <Form.List name={[field.name, "webhooks"]}>
                                {(webhookFields, { add, remove }) => (
                                  <Stack>
                                    {webhookFields.map((webhookField) => {
                                      const webhookValues = unitForm.getFieldValue([
                                        "realUserSeriesPushes",
                                        field.name,
                                        "webhooks",
                                        webhookField.name,
                                      ]) as AnyRecord | undefined;
                                      const isExisting =
                                        typeof webhookValues?.existingIndex === "number";
                                      // 标记删除的行必须保持挂载（仅隐藏），否则其表单项会注销，
                                      // 导致 getFieldsValue 重建数组时产生空洞（序列化后变成 null）。
                                      const isMarkedForRemoval = webhookValues?.remove === true;
                                      return (
                                      <Row
                                        key={webhookField.key}
                                        gutter={8}
                                        align="middle"
                                        style={{
                                          marginBottom: 8,
                                          ...(isMarkedForRemoval ? { display: "none" } : null),
                                        }}
                                      >
                                        {isExisting ? (
                                          <Form.Item
                                            name={[webhookField.name, "existingIndex"]}
                                            hidden
                                          >
                                            <Input />
                                          </Form.Item>
                                        ) : null}
                                        <Form.Item
                                          name={[webhookField.name, "remove"]}
                                          hidden
                                          valuePropName="checked"
                                        >
                                          <Switch />
                                        </Form.Item>
                                        <Col flex="auto">
                                          <Form.Item
                                            name={[webhookField.name, "url"]}
                                            rules={[
                                              {
                                                type: "url",
                                                message: "请输入有效的飞书 Webhook 地址",
                                              },
                                            ]}
                                            style={{ marginBottom: 0 }}
                                          >
                                            <Input.Password
                                              autoComplete="new-password"
                                              placeholder={
                                                isExisting
                                                  ? "已配置（留空保留 / 输入新地址替换）"
                                                  : "请输入飞书 Webhook 地址"
                                              }
                                            />
                                          </Form.Item>
                                        </Col>
                                        <Col>
                                          {webhookFields.length > 1 || isExisting ? (
                                            <Button
                                              type="text"
                                              danger
                                              icon={<DeleteOutlined />}
                                              onClick={() => {
                                                if (isExisting) {
                                                  unitForm.setFieldValue(
                                                    [
                                                      "realUserSeriesPushes",
                                                      field.name,
                                                      "webhooks",
                                                      webhookField.name,
                                                      "remove",
                                                    ],
                                                    true
                                                  );
                                                  return;
                                                }
                                                remove(webhookField.name);
                                              }}
                                            />
                                          ) : null}
                                        </Col>
                                      </Row>
                                      );
                                    })}
                                    <Button
                                      type="dashed"
                                      icon={<PlusOutlined />}
                                      onClick={() => add({ url: "", remove: false })}
                                      block
                                    >
                                      添加 Webhook
                                    </Button>
                                  </Stack>
                                )}
                              </Form.List>
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={5}>
                            <Form.Item
                              name={[field.name, "enabled"]}
                              label="推送"
                              valuePropName="checked"
                            >
                              <Switch />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) =>
                            getFieldValue([
                              "realUserSeriesPushes",
                              field.name,
                              "hasWebhook",
                            ]) ? (
                              <Form.Item
                                name={[field.name, "clearWebhooks"]}
                                valuePropName="checked"
                                style={{ marginBottom: 0 }}
                              >
                                <Checkbox>清除该系列全部 Webhook，并关闭推送</Checkbox>
                              </Form.Item>
                            ) : null
                          }
                        </Form.Item>
                      </div>
                    ))
                  )}
                </Stack>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item hidden name="hasChurnWebhook" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="流失用户通知 Webhook">
            <Form.Item name="churnWebhookUrl" noStyle rules={[{ type: "url", message: "请输入有效的 Webhook 地址" }]}>
              <Input.Password autoComplete="new-password" placeholder="留空保留已配置地址" />
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) =>
                getFieldValue("hasChurnWebhook") ? (
                  <Form.Item name="clearChurnWebhook" valuePropName="checked" style={{ marginBottom: 0 }}>
                    <Checkbox>清除已配置的 Webhook，并关闭通知</Checkbox>
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="新增分组"
        open={groupModalOpen}
        onCancel={() => setGroupModalOpen(false)}
        onOk={() => groupForm.submit()}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={groupForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await trpc.fadan.businessUnits.createGroup.mutate(values);
              message.success("分组已创建");
              setGroupModalOpen(false);
              loadData();
            } catch (error: any) {
              message.error(error?.message || "创建分组失败");
            }
          }}
        >
          <Form.Item name="businessUnitId" hidden><Input /></Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: "请输入分组名称" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量分配群角色"
        open={batchRoomModalOpen}
        onCancel={() => setBatchRoomModalOpen(false)}
        onOk={() => batchRoomForm.submit()}
        okText="批量保存"
        cancelText="取消"
        okButtonProps={{ disabled: batchPreview.matched.length === 0 || !!batchPreview.error }}
      >
        <Form
          form={batchRoomForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              const result = await trpc.fadan.businessUnits.batchUpsertRooms.mutate(values);
              const unmatchedHint = result.unmatchedCodes.length
                ? `，${result.unmatchedCodes.length} 个编号未匹配到群`
                : "";
              message.success(`已分配 ${result.matchedCount} 个群角色${unmatchedHint}`);
              setBatchRoomModalOpen(false);
              loadData();
            } catch (error: any) {
              message.error(error?.message || "批量分配失败");
            }
          }}
        >
          <Form.Item name="businessUnitId" hidden><Input /></Form.Item>
          <Form.Item name="groupId" label="分组" rules={[{ required: true, message: "请选择分组" }]}>
            <Select options={groupOptions} />
          </Form.Item>
          <Form.Item
            name="pattern"
            label="群编号规则"
            rules={[{ required: true, message: "请输入群编号匹配规则" }]}
            extra="支持范围（H001-H040、SVIP001-SVIP015）、逗号分隔（H001,H005）或组合（H001-H010,H020）。可匹配「❤️❤️黄先生 SVIP【专属好物】001」这类群名"
          >
            <Input placeholder="例如：SVIP001-SVIP015" />
          </Form.Item>
          <Form.Item
            name="nickname"
            label="昵称筛选"
            extra="可选，仅分配群昵称包含该文字的群"
          >
            <Input allowClear placeholder="例如：张三" />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true, message: "请选择角色" }]}>
            <Select options={roleOptions} />
          </Form.Item>
          <div style={{ marginTop: 8 }}>
            {batchPreview.error ? (
              <Text type="danger">{batchPreview.error}</Text>
            ) : batchPattern.trim() ? (
              <Stack size={8}>
                <Text type="secondary">
                  已解析 {batchPreview.codes.length} 个编号，匹配到 {batchPreview.matched.length} 个群
                  {batchNickname.trim() ? `（昵称含「${batchNickname.trim()}」）` : ""}
                </Text>
                {batchPreview.unmatchedCodes.length > 0 && (
                  <Text type="warning">
                    未匹配编号：{batchPreview.unmatchedCodeRanges.slice(0, 20).join("、")}
                    {batchPreview.unmatchedCodeRanges.length > 20 ? "..." : ""}
                  </Text>
                )}
                {batchPreview.matched.length > 0 && (
                  <div style={{ maxHeight: 160, overflow: "auto", padding: "8px 12px", background: "#f8fafc", borderRadius: 8 }}>
                    {batchPreview.matched.slice(0, 30).map((room) => (
                      <div key={room.roomId}>
                        <Text>{room.roomName || room.roomId}</Text>
                      </div>
                    ))}
                    {batchPreview.matched.length > 30 && (
                      <Text type="secondary">还有 {batchPreview.matched.length - 30} 个群未展示</Text>
                    )}
                  </div>
                )}
              </Stack>
            ) : (
              <Text type="secondary">输入规则后可预览匹配结果</Text>
            )}
          </div>
        </Form>
      </Modal>

      <Modal
        title={`批量改角色（${selectedRowKeys.length} 项）`}
        open={batchRoleModalOpen}
        onCancel={() => setBatchRoleModalOpen(false)}
        onOk={() => batchRoleForm.submit()}
        okText="确认修改"
        cancelText="取消"
      >
        <Form
          form={batchRoleForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              const result = await trpc.fadan.businessUnits.batchUpdateRoomRoles.mutate({
                ids: selectedRowKeys,
                role: values.role,
              });
              const skippedHint = result.skippedCount > 0 ? `，${result.skippedCount} 项已是该角色` : "";
              message.success(`已修改 ${result.updatedCount} 个群角色${skippedHint}`);
              setBatchRoleModalOpen(false);
              setSelectedRowKeys([]);
              loadData();
            } catch (error: any) {
              message.error(error?.message || "批量修改角色失败");
            }
          }}
        >
          <Form.Item name="role" label="新角色" rules={[{ required: true, message: "请选择角色" }]}>
            <Select options={roleOptions} placeholder="选择目标角色" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`批量设置分组（${selectedRowKeys.length} 项）`}
        open={batchGroupModalOpen}
        onCancel={() => setBatchGroupModalOpen(false)}
        onOk={() => batchGroupForm.submit()}
        okText="确认修改"
        cancelText="取消"
      >
        <Form
          form={batchGroupForm}
          layout="vertical"
          onFinish={async (values) => {
            const newGroupName = String(values.newGroupName || "").trim();
            if (!values.groupId && !newGroupName) {
              message.error("请选择分组或输入新增分组名称");
              return;
            }
            if (newGroupName && !selectedUnitId) {
              message.error("请先选择业务单元");
              return;
            }

            try {
              let groupId = values.groupId;
              if (newGroupName) {
                const existingGroup = (selectedUnit?.groups || []).find(
                  (group: AnyRecord) => String(group.name || "").trim() === newGroupName
                );
                if (existingGroup) {
                  groupId = existingGroup.id;
                } else {
                  const group = await trpc.fadan.businessUnits.createGroup.mutate({
                    businessUnitId: selectedUnitId!,
                    name: newGroupName,
                    description: "",
                    sort: 0,
                  });
                  groupId = group.id;
                }
              }
              const result = await trpc.fadan.businessUnits.batchUpdateRoomGroups.mutate({
                ids: selectedRowKeys,
                groupId,
              });
              const skippedHint = result.skippedCount > 0 ? `，${result.skippedCount} 项已在该分组` : "";
              message.success(`已设置 ${result.updatedCount} 个群角色分组${skippedHint}`);
              setBatchGroupModalOpen(false);
              setSelectedRowKeys([]);
              loadData();
            } catch (error: any) {
              message.error(error?.message || "批量设置分组失败");
            }
          }}
        >
          <Form.Item name="groupId" label="已有分组" extra="选择已有分组，或在下方输入新分组名称。填写新分组名称时将优先新建并设置。">
            <Select allowClear options={groupOptions} placeholder="选择目标分组" />
          </Form.Item>
          <Form.Item name="newGroupName" label="新增分组名称">
            <Input allowClear maxLength={80} placeholder="例如：新增分组" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={seriesGapReport ? `${seriesGapReport.seriesLetter}系列 序号监测` : "序号监测"}
        open={!!seriesGapReport}
        onCancel={() => setSeriesGapReport(null)}
        footer={null}
        width={560}
      >
        {seriesGapReport ? (
          <Stack size={16}>
            <div>
              <Text>
                已配置 {seriesGapReport.parsedCount} 个可解析编号
                {seriesGapReport.expectedMax > 0
                  ? `，序号范围 ${seriesGapReport.prefix}${String(seriesGapReport.expectedMin).padStart(seriesGapReport.width, "0")}–${seriesGapReport.prefix}${String(seriesGapReport.expectedMax).padStart(seriesGapReport.width, "0")}`
                  : ""}
              </Text>
              {seriesGapReport.isComplete ? (
                <div style={{ marginTop: 8 }}>
                  <Tag color="success">序号连续完整</Tag>
                </div>
              ) : (
                <div style={{ marginTop: 8 }}>
                  <Text type="danger" strong>
                    缺失 {seriesGapReport.missingCodes.length} 个编号
                  </Text>
                </div>
              )}
            </div>
            {seriesGapReport.missingRanges.length > 0 && (
              <div>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  缺失编号
                </Text>
                <Space size={[8, 8]} wrap>
                  {seriesGapReport.missingRanges.map((range) => (
                    <Tag key={range} color="error">
                      {range}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}
            {seriesGapReport.unparseableRooms.length > 0 && (
              <div>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  无法解析编号（{seriesGapReport.unparseableRooms.length}）
                </Text>
                <div style={{ maxHeight: 120, overflow: "auto", padding: "8px 12px", background: "#f8fafc", borderRadius: 8 }}>
                  {seriesGapReport.unparseableRooms.map((room) => (
                    <div key={room.roomId}>
                      <Text type="secondary">{room.roomName || room.roomId}</Text>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Stack>
        ) : null}
      </Modal>

      <Modal
        title="分配群角色"
        open={roomModalOpen}
        onCancel={() => setRoomModalOpen(false)}
        onOk={() => roomForm.submit()}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={roomForm}
          layout="vertical"
          onFinish={async (values) => {
            const selectedRoomIds = Array.isArray(values.roomIds)
              ? (values.roomIds as unknown[])
              : [];
            const roomIds: string[] = Array.from(
              new Set(
                selectedRoomIds
                  .map((roomId) => String(roomId ?? "").trim())
                  .filter((roomId) => roomId.length > 0)
              )
            );
            if (roomIds.length === 0) {
              message.error("请选择至少一个群聊");
              return;
            }
            try {
              const result = await trpc.fadan.businessUnits.batchUpsertRoomsByIds.mutate({
                businessUnitId: values.businessUnitId,
                groupId: values.groupId,
                role: values.role,
                roomIds,
              });
              const notFoundHint = result.notFoundRoomIds?.length
                ? `，${result.notFoundRoomIds.length} 个群不存在或不可用`
                : "";
              message.success(`已分配 ${result.assignedCount} 个群角色${notFoundHint}`);
              setRoomModalOpen(false);
              loadData();
            } catch (error: any) {
              message.error(error?.message || "保存群角色失败");
            }
          }}
        >
          <Form.Item name="businessUnitId" hidden><Input /></Form.Item>
          <Form.Item name="groupId" label="分组" rules={[{ required: true, message: "请选择分组" }]}>
            <Select options={groupOptions} />
          </Form.Item>
          <Form.Item
            name="roomIds"
            label="群聊"
            rules={[{ required: true, type: "array", min: 1, message: "请选择至少一个群聊" }]}
          >
            <Select
              mode="multiple"
              showSearch={{ optionFilterProp: "label" }}
              options={roomOptions}
              placeholder="搜索并选择群名称"
              maxTagCount="responsive"
              allowClear
            />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true, message: "请选择角色" }]}>
            <Select options={roleOptions} />
          </Form.Item>
        </Form>
      </Modal>
    </Stack>
  );
}

const messageTypeOptions = [
  { label: "text", value: "text" },
  { label: "image", value: "image" },
  { label: "语音", value: "voice" },
  { label: "video", value: "video" },
  { label: "file", value: "file" },
  { label: "link", value: "link" },
  { label: "weapp", value: "weapp" },
  { label: "emotion", value: "emotion" },
  { label: "revoke", value: "revoke" },
  { label: "chatrecord", value: "chatrecord" },
];

const mediaPreviewTypes = new Set(["image", "emotion"]);

function archiveMediaUrl(record: { sourceId?: string; mediaMd5?: string; msgtype?: string }) {
  if (!record.sourceId || !record.mediaMd5 || !mediaPreviewTypes.has(record.msgtype || "")) return "";
  return `/api/archive-media/${encodeURIComponent(record.sourceId)}/${encodeURIComponent(record.mediaMd5)}`;
}

function MessageRecordContent({
  record,
  onPreview,
}: {
  record: AnyRecord;
  onPreview: (preview: { url: string; title: string }) => void;
}) {
  const isMediaPreview = mediaPreviewTypes.has(record.msgtype || "");
  const text = (isMediaPreview ? record.matchKey : "") || record.content || record.mediaKey || "-";
  const mediaUrl = archiveMediaUrl(record);
  return (
    <div className="message-record-content">
      {mediaUrl ? (
        <button
          type="button"
          className="message-record-media-link"
          onClick={() => onPreview({ url: mediaUrl, title: text })}
        >
          <img src={mediaUrl} alt={text} className="message-record-media-preview" loading="lazy" />
        </button>
      ) : null}
      <Text ellipsis className="message-record-content-text">{text}</Text>
    </div>
  );
}

export function MessageRecordsPage() {
  const { message } = App.useApp();
  const [items, setItems] = useState<AnyRecord[]>([]);
  const [sources, setSources] = useState<AnyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    keyword: "",
    sourceId: "",
    roomId: "",
    msgtypes: ["text", "image", "emotion", "voice", "link"],
  });
  const [total, setTotal] = useState(0);
  const [mediaModal, setMediaModal] = useState<{ url: string; title: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sourceList] = await Promise.all([
        trpc.fadan.messages.query({
          ...filters,
          sourceId: filters.sourceId || undefined,
          roomId: filters.roomId || undefined,
          msgtypes: filters.msgtypes.length > 0 ? filters.msgtypes : undefined,
          keyword: filters.keyword || undefined,
        }),
        trpc.fadan.sources.list.query(),
      ]);
      setItems(list.items);
      setTotal(list.total);
      setSources(sourceList);
    } catch (error: any) {
      message.error(error?.message || "加载消息失败");
    } finally {
      setLoading(false);
    }
  }, [filters, message]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns: ColumnsType<AnyRecord> = [
    { title: "时间", dataIndex: "messageTime", key: "messageTime", width: 180, render: formatTime },
    { title: "群聊", dataIndex: "roomName", key: "roomName", width: 220, render: (_, record) => <Text copyable={{ text: record.roomId }}>{record.roomName || record.roomId}</Text> },
    { title: "发送人", dataIndex: "senderName", key: "senderName", width: 160, render: (_, record) => <Text copyable={{ text: record.senderId }}>{record.senderName || record.senderId}</Text> },
    { title: "类型", dataIndex: "msgtype", key: "msgtype", width: 100, render: (value) => <Tag>{value || "unknown"}</Tag> },
    { title: "内容", dataIndex: "content", key: "content", render: (_, record) => <MessageRecordContent record={record} onPreview={setMediaModal} /> },
    { title: "来源", dataIndex: "ingestSource", key: "ingestSource", width: 100, render: (value) => <Tag color={value === "push" ? "blue" : "cyan"}>{value === "push" ? "推送" : "补偿"}</Tag> },
  ];

  return (
    <Stack size={20} className="fade-in">
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>消息记录</Title>
          <Text type="secondary">展示 24 小时内保留的会话存档消息，昵称来自客户群画像缓存。</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新</Button>
      </div>
      <Card variant="borderless" className="shadow-sm">
        <div className="monitor-toolbar">
          <Input.Search
            allowClear
            placeholder="搜索内容、发送人或群名"
            style={{ width: 260 }}
            value={filters.keyword}
            onChange={(event) => setFilters((prev) => ({ ...prev, keyword: event.target.value, page: 1 }))}
            onSearch={loadData}
          />
          <Select
            allowClear
            placeholder="存档源"
            style={{ width: 220 }}
            value={filters.sourceId || undefined}
            options={sources.map((item) => ({ label: item.name, value: item.id }))}
            onChange={(value) => setFilters((prev) => ({ ...prev, sourceId: value || "", page: 1 }))}
          />
          <Input
            placeholder="群聊 ID"
            style={{ width: 220 }}
            value={filters.roomId}
            onChange={(event) => setFilters((prev) => ({ ...prev, roomId: event.target.value, page: 1 }))}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="消息类型"
            style={{ minWidth: 200 }}
            options={messageTypeOptions}
            value={filters.msgtypes}
            onChange={(values) => setFilters((prev) => ({ ...prev, msgtypes: values, page: 1 }))}
            maxTagCount="responsive"
          />
        </div>
      </Card>
      <Card variant="borderless" className="shadow-sm">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total,
            onChange: (page, pageSize) => setFilters((prev) => ({ ...prev, page, limit: pageSize })),
          }}
        />
      </Card>
      <Modal
        open={!!mediaModal}
        title={mediaModal?.title || "媒体预览"}
        onCancel={() => setMediaModal(null)}
        footer={null}
        width="min(900px, 92vw)"
        centered
      >
        {mediaModal ? (
          <div className="dispatch-media-preview-modal">
            <img src={mediaModal.url} alt={mediaModal.title} className="dispatch-media-preview-modal-image" />
          </div>
        ) : null}
      </Modal>
    </Stack>
  );
}

export function BroadcastStatsPage() {
  const { message } = App.useApp();
  const [windowMinutes, setWindowMinutes] = useState(60);
  const [items, setItems] = useState<AnyRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await trpc.fadan.broadcastStats.query({ windowMinutes }));
    } catch (error: any) {
      message.error(error?.message || "加载发单统计失败");
    } finally {
      setLoading(false);
    }
  }, [message, windowMinutes]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns: ColumnsType<AnyRecord> = [
    { title: "内容", dataIndex: "content", key: "content", render: (value) => <Text ellipsis style={{ maxWidth: 520 }}>{value}</Text> },
    { title: "类型", dataIndex: "msgtype", key: "msgtype", width: 100, render: (value) => <Tag>{value}</Tag> },
    { title: "覆盖群数", dataIndex: "roomCount", key: "roomCount", width: 120, sorter: (a, b) => a.roomCount - b.roomCount },
    { title: "消息数", dataIndex: "messageCount", key: "messageCount", width: 110, sorter: (a, b) => a.messageCount - b.messageCount },
    { title: "发送人", dataIndex: "senders", key: "senders", render: (value: string[]) => (value || []).filter(Boolean).slice(0, 6).join("、") || "-" },
    { title: "最近出现", dataIndex: "lastMessageAt", key: "lastMessageAt", width: 180, render: formatTime },
  ];

  return (
    <Stack size={20} className="fade-in">
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>发单统计</Title>
          <Text type="secondary">按归一化文案和媒体标识统计多群扩散情况。</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新</Button>
      </div>
      <Card variant="borderless" className="shadow-sm">
        <Segmented
          value={windowMinutes}
          options={windowOptions.filter((item) => item.value >= 30)}
          onChange={(value) => setWindowMinutes(Number(value))}
        />
      </Card>
      <Card variant="borderless" className="shadow-sm">
        <Table rowKey="key" columns={columns} dataSource={items} loading={loading} scroll={{ x: 980 }} />
      </Card>
    </Stack>
  );
}

export function RoomListPage() {
  const { message } = App.useApp();
  const { isAdmin } = useTenantContext();
  const [items, setItems] = useState<AnyRecord[]>([]);
  const [sources, setSources] = useState<AnyRecord[]>([]);
  const [businessUnits, setBusinessUnits] = useState<AnyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    keyword: "",
    sourceId: "",
    unnamedOnly: false,
  });
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedRoomMap, setSelectedRoomMap] = useState<Record<string, AnyRecord>>({});
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AnyRecord | null>(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [skippingChatId, setSkippingChatId] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignForm] = Form.useForm();
  const [importOpen, setImportOpen] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<AnyRecord | null>(null);
  const [inferOpen, setInferOpen] = useState(false);
  const [inferTarget, setInferTarget] = useState<AnyRecord | null>(null);
  const [inferPreview, setInferPreview] = useState<AnyRecord | null>(null);
  const [inferLoading, setInferLoading] = useState(false);
  const [inferApplying, setInferApplying] = useState(false);
  const [batchInferOpen, setBatchInferOpen] = useState(false);
  const [batchInferLoading, setBatchInferLoading] = useState(false);
  const [batchInferResult, setBatchInferResult] = useState<AnyRecord | null>(null);
  const [selectedInferWxGroupId, setSelectedInferWxGroupId] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [result, sourceList] = await Promise.all([
        trpc.fadan.roomProfiles.list.query({
          page: filters.page,
          limit: filters.limit,
          keyword: filters.keyword || undefined,
          sourceId: filters.sourceId || undefined,
          unnamedOnly: filters.unnamedOnly || undefined,
        }),
        trpc.fadan.sources.list.query().catch(() => [] as AnyRecord[]),
      ]);
      setItems(result.items);
      setTotal(result.total);
      setSources(sourceList);
    } catch (error: any) {
      message.error(error?.message || "加载群列表失败");
    } finally {
      setLoading(false);
    }
  }, [filters, message]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!isAdmin) return;
    void trpc.fadan.businessUnits.list
      .query()
      .then((list) => setBusinessUnits(list))
      .catch(() => setBusinessUnits([]));
  }, [isAdmin]);

  const isUnnamedRoom = useCallback((record: AnyRecord) => {
    const name = String(record.name || "").trim();
    const chatId = String(record.chatId || "").trim();
    return !name || name === chatId;
  }, []);

  const isRoomWxGroupMatched = useCallback((record: AnyRecord) => {
    return Boolean(String(record.wxGroupId || "").trim());
  }, []);

  const selectedRooms = useMemo(
    () =>
      selectedRowKeys
        .map((key) => selectedRoomMap[key])
        .filter((room): room is AnyRecord => Boolean(room?.chatId)),
    [selectedRowKeys, selectedRoomMap]
  );

  const assignUnitId = Form.useWatch("businessUnitId", assignForm);
  const assignGroupOptions = useMemo(() => {
    const unit = businessUnits.find((item) => item.id === assignUnitId);
    return (unit?.groups || []).map((group: AnyRecord) => ({
      label: group.name,
      value: group.id,
    }));
  }, [businessUnits, assignUnitId]);

  const openEditModal = (record: AnyRecord) => {
    setEditTarget(record);
    setEditName(isUnnamedRoom(record) ? "" : String(record.name || ""));
    setEditOpen(true);
  };

  const handleSaveManualName = async () => {
    if (!editTarget?.chatId) return;
    const trimmed = editName.trim();
    if (!trimmed) {
      message.warning("请输入群昵称");
      return;
    }
    if (trimmed === editTarget.chatId) {
      message.warning("群昵称不能与群 ID 相同");
      return;
    }
    setSaving(true);
    try {
      await trpc.fadan.roomProfiles.setManualName.mutate({
        chatId: editTarget.chatId,
        name: trimmed,
      });
      message.success("群昵称已保存，后续将不再自动刷新");
      setEditOpen(false);
      setEditTarget(null);
      setEditName("");
      await loadData();
    } catch (error: any) {
      message.error(error?.message || "保存失败");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkSkipped = async (record: AnyRecord) => {
    if (!record?.chatId) return;
    setSkippingChatId(record.chatId);
    try {
      await trpc.fadan.roomProfiles.markSkipped.mutate({ chatId: record.chatId });
      message.success("已标记跳过自动刷新");
      await loadData();
    } catch (error: any) {
      message.error(error?.message || "标记失败");
    } finally {
      setSkippingChatId("");
    }
  };

  const openAssignModal = () => {
    if (selectedRooms.length === 0) {
      message.warning("请先勾选要添加的群");
      return;
    }
    if (businessUnits.length === 0) {
      message.warning("暂无业务单元，请先在业务单元页创建");
      return;
    }
    const firstUnit = businessUnits[0];
    const firstGroup = firstUnit?.groups?.[0];
    assignForm.setFieldsValue({
      businessUnitId: firstUnit?.id,
      groupId: firstGroup?.id,
      role: "target",
    });
    setAssignOpen(true);
  };

  const handleAssignToBusinessUnit = async (values: AnyRecord) => {
    const roomIds = selectedRooms.map((room) => String(room.chatId)).filter(Boolean);
    if (roomIds.length === 0) {
      message.warning("请先勾选要添加的群");
      return;
    }
    setAssigning(true);
    try {
      const result = await trpc.fadan.businessUnits.batchUpsertRoomsByIds.mutate({
        businessUnitId: values.businessUnitId,
        groupId: values.groupId,
        role: values.role,
        roomIds,
      });
      const notFoundHint = result.notFoundRoomIds?.length
        ? `，${result.notFoundRoomIds.length} 个群不存在或不可用`
        : "";
      message.success(`已添加 ${result.assignedCount} 个群到业务单元${notFoundHint}`);
      setAssignOpen(false);
      setSelectedRowKeys([]);
      setSelectedRoomMap({});
    } catch (error: any) {
      message.error(error?.message || "添加到业务单元失败");
    } finally {
      setAssigning(false);
    }
  };

  const parseImportItems = (raw: string) => {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      throw new Error("JSON 必须是数组格式");
    }
    return parsed as Array<Record<string, unknown>>;
  };

  const importPreview = useMemo(() => {
    if (!importJson.trim()) return null;
    try {
      const items = parseImportItems(importJson);
      const valid = items.filter((item) => {
        const chatId = String(item.chat_id ?? item.chatId ?? "").trim();
        const name = String(item.room_name ?? item.name ?? "").trim();
        return Boolean(chatId && name);
      });
      return { total: items.length, valid: valid.length, invalid: items.length - valid.length };
    } catch {
      return { total: 0, valid: 0, invalid: 0, error: true };
    }
  }, [importJson]);

  const handleImportJson = async () => {
    let items: Array<Record<string, unknown>>;
    try {
      items = parseImportItems(importJson);
    } catch (error: any) {
      message.error(error?.message || "JSON 解析失败");
      return;
    }
    if (items.length === 0) {
      message.warning("导入列表为空");
      return;
    }
    setImporting(true);
    try {
      const result = await trpc.fadan.roomProfiles.batchImportManualNames.mutate({ items });
      setImportResult(result);
      message.success(
        `导入完成：成功 ${result.summary.updated} 条，未找到 ${result.summary.notFound} 条`
      );
      if (result.summary.updated > 0) {
        await loadData();
      }
    } catch (error: any) {
      message.error(error?.message || "批量导入失败");
    } finally {
      setImporting(false);
    }
  };

  const handleImportFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setImportJson(String(reader.result || ""));
      setImportResult(null);
    };
    reader.readAsText(file);
    return false;
  };

  const openInferModal = async (record: AnyRecord) => {
    if (!record?.chatId) return;
    setInferTarget(record);
    setInferOpen(true);
    setInferPreview(null);
    setSelectedInferWxGroupId("");
    setInferLoading(true);
    try {
      const preview = await trpc.fadan.roomProfiles.inferGuziNamePreview.query({
        chatId: record.chatId,
      });
      setInferPreview(preview);
      const candidates = Array.isArray(preview.candidates) ? preview.candidates : [];
      const matched = candidates.find((item) => item.wxGroupId === record.wxGroupId);
      setSelectedInferWxGroupId(
        String(matched?.wxGroupId || candidates[0]?.wxGroupId || "")
      );
    } catch (error: any) {
      message.error(error?.message || "加载匹配预览失败");
      setInferOpen(false);
      setInferTarget(null);
    } finally {
      setInferLoading(false);
    }
  };

  const handleApplyInfer = async (force = false, wxGroupId = selectedInferWxGroupId) => {
    if (!inferTarget?.chatId) return;
    if (!wxGroupId) {
      message.warning("请先选择一个候选群");
      return;
    }
    setInferApplying(true);
    try {
      await trpc.fadan.roomProfiles.inferGuziName.mutate({
        chatId: inferTarget.chatId,
        wxGroupId,
        force,
      });
      message.success("群信息匹配已应用");
      setInferOpen(false);
      setInferTarget(null);
      setInferPreview(null);
      setSelectedInferWxGroupId("");
      await loadData();
    } catch (error: any) {
      const errorMessage = error?.message || "应用匹配失败";
      if (!force && errorMessage.includes("强制覆盖")) {
        Modal.confirm({
          title: "该群已有群名，确认覆盖？",
          content: errorMessage,
          okText: "强制覆盖",
          cancelText: "取消",
          onOk: () => handleApplyInfer(true, wxGroupId),
        });
        return;
      }
      message.error(errorMessage);
    } finally {
      setInferApplying(false);
    }
  };

  const handleBatchInfer = async () => {
    setBatchInferLoading(true);
    setBatchInferResult(null);
    setBatchInferOpen(true);
    try {
      const result = await trpc.fadan.roomProfiles.batchInferGuziNames.mutate({});
      setBatchInferResult(result);
      if (result.applied > 0) {
        await loadData();
      }
    } catch (error: any) {
      message.error(error?.message || "批量匹配失败");
      setBatchInferOpen(false);
    } finally {
      setBatchInferLoading(false);
    }
  };

  const columns: ColumnsType<AnyRecord> = [
    {
      title: "群昵称",
      dataIndex: "name",
      key: "name",
      width: 260,
      render: (value, record) => {
        const unnamed = isUnnamedRoom(record);
        return (
          <Text
            copyable={{ text: record.chatId }}
            type={unnamed ? "secondary" : undefined}
          >
            {unnamed ? record.chatId : value}
          </Text>
        );
      },
    },
    {
      title: "群 ID",
      dataIndex: "chatId",
      key: "chatId",
      width: 260,
      render: (value) => <Text copyable>{value}</Text>,
    },
    {
      title: "wx_group_id",
      dataIndex: "wxGroupId",
      key: "wxGroupId",
      width: 240,
      render: (value, record) => {
        const wxGroupId = String(value || "").trim();
        if (!isRoomWxGroupMatched(record)) return "-";
        return <Text copyable>{wxGroupId}</Text>;
      },
    },
    {
      title: "成员数",
      dataIndex: "memberCount",
      key: "memberCount",
      width: 90,
      align: "right",
    },
    {
      title: "群主",
      dataIndex: "owner",
      key: "owner",
      width: 180,
      render: (value) => (value ? <Text copyable>{value}</Text> : "-"),
    },
    {
      title: "存档源",
      dataIndex: "sourceName",
      key: "sourceName",
      width: 160,
      render: (value, record) => value || record.sourceId || "-",
    },
    {
      title: "Tenant",
      dataIndex: "tenantId",
      key: "tenantId",
      width: 140,
      render: (value) => <Text copyable>{value}</Text>,
    },
    {
      title: "刷新状态",
      dataIndex: "profileRefreshSkipped",
      key: "profileRefreshSkipped",
      width: 110,
      render: (value, record) =>
        value ? (
          <Tooltip title={record.profileRefreshSkipReason || "已跳过刷新"}>
            <Tag color="default">已跳过</Tag>
          </Tooltip>
        ) : (
          <Tag color="success">正常</Tag>
        ),
    },
    {
      title: "最近拉取",
      dataIndex: "fetchedAt",
      key: "fetchedAt",
      width: 180,
      render: formatTime,
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: formatTime,
    },
    ...(isAdmin
      ? [
          {
            title: "操作",
            key: "actions",
            fixed: "right" as const,
            width: 280,
            render: (_: unknown, record: AnyRecord) => (
              <Space size={0}>
                <Button type="link" size="small" icon={<ThunderboltOutlined />} onClick={() => openInferModal(record)}>
                  {isRoomWxGroupMatched(record) ? "重新匹配" : "匹配群信息"}
                </Button>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
                  填群名
                </Button>
                {record.profileRefreshSkipped ? null : (
                  <Popconfirm
                    title="标记为已跳过？"
                    description="之后将不再自动刷新该群画像"
                    okText="确认"
                    cancelText="取消"
                    onConfirm={() => handleMarkSkipped(record)}
                  >
                    <Button type="link" size="small" loading={skippingChatId === record.chatId}>
                      标记已跳过
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <Stack size={20} className="fade-in">
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            群列表
          </Title>
          <Text type="secondary">
            展示已入库的客户群。消息带 roomId 即先入库；群昵称由画像异步刷新补全。管理员可手动填群名、标记跳过，并多选添加到业务单元。
          </Text>
        </div>
        <Space>
          {isAdmin && selectedRooms.length > 0 && (
            <Button type="primary" icon={<TeamOutlined />} onClick={openAssignModal}>
              添加到业务单元（{selectedRooms.length}）
            </Button>
          )}
          {isAdmin && filters.unnamedOnly && (
            <>
              <Button
                icon={<ThunderboltOutlined />}
                loading={batchInferLoading}
                onClick={handleBatchInfer}
              >
                一键匹配群信息
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={() => {
                  setImportOpen(true);
                  setImportJson("");
                  setImportResult(null);
                }}
              >
                导入 JSON
              </Button>
            </>
          )}
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            刷新
          </Button>
        </Space>
      </div>
      <Card variant="borderless" className="shadow-sm">
        <div className="monitor-toolbar">
          <Input.Search
            allowClear
            placeholder="搜索群昵称"
            style={{ width: 260 }}
            value={filters.keyword}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, keyword: event.target.value, page: 1 }))
            }
            onSearch={loadData}
          />
          {sources.length > 0 && (
            <Select
              allowClear
              placeholder="存档源"
              style={{ width: 220 }}
              value={filters.sourceId || undefined}
              options={sources.map((item) => ({ label: item.name, value: item.id }))}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, sourceId: value || "", page: 1 }))
              }
            />
          )}
          <Space size={8}>
            <Switch
              checked={filters.unnamedOnly}
              onChange={(checked) =>
                setFilters((prev) => ({ ...prev, unnamedOnly: checked, page: 1 }))
              }
            />
            <Text>仅看无昵称</Text>
          </Space>
          {isAdmin && selectedRooms.length > 0 && (
            <Button
              type="link"
              onClick={() => {
                setSelectedRowKeys([]);
                setSelectedRoomMap({});
              }}
            >
              清空选择
            </Button>
          )}
        </div>
      </Card>
      <Card variant="borderless" className="shadow-sm">
        <Table
          rowKey="chatId"
          columns={columns}
          dataSource={items}
          loading={loading}
          scroll={{ x: 1920 }}
          rowSelection={
            isAdmin
              ? {
                  selectedRowKeys,
                  preserveSelectedRowKeys: true,
                  onChange: (keys, rows) => {
                    const nextKeys = keys.map(String);
                    setSelectedRowKeys(nextKeys);
                    setSelectedRoomMap((prev) => {
                      const next = { ...prev };
                      for (const key of Object.keys(next)) {
                        if (!nextKeys.includes(key)) delete next[key];
                      }
                      for (const row of rows) {
                        if (row?.chatId) next[String(row.chatId)] = row;
                      }
                      return next;
                    });
                  },
                }
              : undefined
          }
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total,
            showSizeChanger: true,
            showTotal: (count) => `共 ${count} 个群`,
            onChange: (page, pageSize) =>
              setFilters((prev) => ({ ...prev, page, limit: pageSize })),
          }}
        />
      </Card>

      <Modal
        title="编辑群昵称"
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditTarget(null);
          setEditName("");
        }}
        onOk={handleSaveManualName}
        confirmLoading={saving}
        okText="保存"
        destroyOnHidden
      >
        <Stack size={12}>
          <Text type="secondary">群 ID：{editTarget?.chatId}</Text>
          <Input
            placeholder="请输入群昵称"
            value={editName}
            onChange={(event) => setEditName(event.target.value)}
            maxLength={128}
            onPressEnter={handleSaveManualName}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            保存后将标记为「手动设置群名」，不再自动刷新覆盖。
          </Text>
        </Stack>
      </Modal>

      <Modal
        title="添加到业务单元"
        open={assignOpen}
        onCancel={() => setAssignOpen(false)}
        onOk={() => assignForm.submit()}
        confirmLoading={assigning}
        okText="添加"
        destroyOnHidden
      >
        <Form form={assignForm} layout="vertical" onFinish={handleAssignToBusinessUnit}>
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            已选 {selectedRooms.length} 个群
          </Text>
          <Form.Item
            name="businessUnitId"
            label="业务单元"
            rules={[{ required: true, message: "请选择业务单元" }]}
          >
            <Select
              options={businessUnits.map((unit) => ({ label: unit.name, value: unit.id }))}
              placeholder="选择业务单元"
              onChange={(value) => {
                const unit = businessUnits.find((item) => item.id === value);
                assignForm.setFieldValue("groupId", unit?.groups?.[0]?.id);
              }}
            />
          </Form.Item>
          <Form.Item name="groupId" label="分组" rules={[{ required: true, message: "请选择分组" }]}>
            <Select
              options={assignGroupOptions}
              placeholder={assignUnitId ? "选择分组" : "请先选择业务单元"}
              disabled={!assignUnitId}
            />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true, message: "请选择角色" }]}>
            <Select options={roleOptions} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="导入 JSON 批量补全群昵称"
        open={importOpen}
        width={720}
        onCancel={() => {
          setImportOpen(false);
          setImportJson("");
          setImportResult(null);
        }}
        onOk={handleImportJson}
        confirmLoading={importing}
        okText="开始导入"
        destroyOnHidden
      >
        <Stack size={16}>
          <Upload accept=".json,application/json" showUploadList={false} beforeUpload={handleImportFile}>
            <Button icon={<FileTextOutlined />}>选择 JSON 文件</Button>
          </Upload>
          <Input.TextArea
            rows={8}
            placeholder={'粘贴 JSON 数组，例如：[{ "chat_id": "wr...", "room_name": "群名称" }]'}
            value={importJson}
            onChange={(event) => {
              setImportJson(event.target.value);
              setImportResult(null);
            }}
          />
          {importPreview && (
            <Text type={importPreview.error ? "danger" : "secondary"}>
              {importPreview.error
                ? "JSON 格式无效"
                : `共 ${importPreview.total} 条，有效 ${importPreview.valid} 条，无效 ${importPreview.invalid} 条`}
            </Text>
          )}
          {importResult && (
            <Stack size={8}>
              <Text>
                成功 {importResult.summary.updated} 条，未找到 {importResult.summary.notFound} 条，无效{" "}
                {importResult.summary.invalid} 条，失败 {importResult.summary.failed} 条
              </Text>
              {importResult.notFound?.length > 0 && (
                <Table
                  size="small"
                  rowKey="chatId"
                  pagination={{ pageSize: 5 }}
                  dataSource={importResult.notFound}
                  columns={[
                    { title: "chat_id", dataIndex: "chatId", key: "chatId", ellipsis: true },
                    { title: "room_name", dataIndex: "name", key: "name", ellipsis: true },
                  ]}
                />
              )}
              {importResult.failed?.length > 0 && (
                <Table
                  size="small"
                  rowKey="chatId"
                  pagination={{ pageSize: 5 }}
                  dataSource={importResult.failed}
                  columns={[
                    { title: "chat_id", dataIndex: "chatId", key: "chatId", ellipsis: true },
                    { title: "room_name", dataIndex: "name", key: "name", ellipsis: true },
                    { title: "原因", dataIndex: "message", key: "message", ellipsis: true },
                  ]}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Modal>

      <Modal
        title={inferTarget && isRoomWxGroupMatched(inferTarget) ? "重新匹配群信息" : "匹配群信息"}
        open={inferOpen}
        width={720}
        onCancel={() => {
          setInferOpen(false);
          setInferTarget(null);
          setInferPreview(null);
          setSelectedInferWxGroupId("");
        }}
        onOk={() => handleApplyInfer(false)}
        okText="应用所选"
        okButtonProps={{ disabled: !selectedInferWxGroupId }}
        confirmLoading={inferApplying}
        destroyOnHidden
      >
        <Stack size={16}>
          <Text type="secondary">群 ID：{inferTarget?.chatId}</Text>
          {inferLoading ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <Spin />
            </div>
          ) : inferPreview ? (
            <>
              <Text>
                非白名单发言人数：{inferPreview.totalNonWhitelistSpeakers}
                {inferPreview.ambiguousSpeakerCount > 0
                  ? `（其中 ${inferPreview.ambiguousSpeakerCount} 人在 Guzi 属于多个群）`
                  : ""}
                。请从下方候选中选择一个群信息应用。
              </Text>
              {inferPreview.candidates?.length === 0 && inferPreview.reason ? (
                <Alert type="warning" showIcon message={inferPreview.reason} />
              ) : null}
              <Table<GuziInferCandidateRow>
                size="small"
                rowKey="wxGroupId"
                pagination={false}
                scroll={{ y: INFER_CANDIDATE_TABLE_SCROLL_Y }}
                dataSource={(inferPreview.candidates as GuziInferCandidateRow[] | undefined) ?? []}
                rowSelection={{
                  type: "radio",
                  selectedRowKeys: selectedInferWxGroupId ? [selectedInferWxGroupId] : [],
                  onChange: (keys) => setSelectedInferWxGroupId(String(keys[0] || "")),
                }}
                onRow={(candidate) => ({
                  onClick: () => setSelectedInferWxGroupId(candidate.wxGroupId),
                  style: { cursor: "pointer" },
                })}
                columns={[
                  { title: "群名", dataIndex: "groupName", key: "groupName", ellipsis: true, render: (v) => v || "-" },
                  { title: "wx_group_id", dataIndex: "wxGroupId", key: "wxGroupId", ellipsis: true },
                  { title: "非白命中人数", dataIndex: "count", key: "count", width: 120 },
                ]}
              />
            </>
          ) : null}
        </Stack>
      </Modal>

      <Modal
        title="批量匹配群信息"
        open={batchInferOpen}
        width={860}
        onCancel={() => {
          setBatchInferOpen(false);
          setBatchInferResult(null);
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setBatchInferOpen(false);
              setBatchInferResult(null);
            }}
          >
            关闭
          </Button>,
        ]}
        destroyOnHidden
      >
        {batchInferLoading ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : batchInferResult ? (
          <Stack size={16}>
            <Text>
              共处理 {batchInferResult.total} 个群，成功 {batchInferResult.applied} 个，跳过{" "}
              {batchInferResult.skipped} 个
            </Text>
            <Table
              size="small"
              rowKey="chatId"
              pagination={{ pageSize: 8 }}
              dataSource={batchInferResult.results || []}
              columns={[
                { title: "群 ID", dataIndex: "chatId", key: "chatId", ellipsis: true, width: 220 },
                { title: "候选数", dataIndex: "candidateCount", key: "candidateCount", width: 80 },
                { title: "群名", dataIndex: "groupName", key: "groupName", ellipsis: true },
                { title: "wx_group_id", dataIndex: "selectedWxGroupId", key: "selectedWxGroupId", ellipsis: true },
                {
                  title: "结果",
                  dataIndex: "applied",
                  key: "applied",
                  width: 80,
                  render: (value) => (value ? <Tag color="success">成功</Tag> : <Tag>跳过</Tag>),
                },
                { title: "原因", dataIndex: "reason", key: "reason", ellipsis: true },
              ]}
            />
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}

export function SpeakerRosterPage() {
  const { message } = App.useApp();
  const [items, setItems] = useState<AnyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    keyword: string;
    taokeId: string;
    isWhitelisted?: boolean;
  }>({ page: 1, limit: 20, keyword: "", taokeId: "" });
  const [total, setTotal] = useState(0);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [taokeIdInput, setTaokeIdInput] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await trpc.fadan.speakerRoster.list.query({
        page: filters.page,
        limit: filters.limit,
        keyword: filters.keyword || undefined,
        taokeId: filters.taokeId || undefined,
        isWhitelisted: filters.isWhitelisted,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error: any) {
      message.error(error?.message || "加载发言名单失败");
    } finally {
      setLoading(false);
    }
  }, [filters, message]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refreshRow = useCallback(
    async (record: AnyRecord) => {
      const id = String(record.id || "");
      if (!id) return;
      setRefreshingId(id);
      try {
        const result = await trpc.fadan.speakerRoster.refresh.mutate({ id });
        message.success(
          result.found
            ? `已重新同步，当前${result.isWhitelisted ? "在白名单" : "不在白名单"}`
            : "产出系统未查到该账号，已保留原有判定"
        );
        await loadData();
      } catch (error: any) {
        message.error(error?.message || "刷新失败");
      } finally {
        setRefreshingId(null);
      }
    },
    [loadData, message]
  );

  const columns: ColumnsType<AnyRecord> = [
    {
      title: "群 ID",
      dataIndex: "roomId",
      key: "roomId",
      width: 220,
      render: (value) => (value ? <Text copyable>{value}</Text> : "-"),
    },
    {
      title: "wx_group_id",
      dataIndex: "wxGroupId",
      key: "wxGroupId",
      width: 240,
      render: (_value, record) => {
        if (record.wxGroupIdResolved && record.wxGroupId) {
          return <Text copyable>{String(record.wxGroupId)}</Text>;
        }
        if (record.wxGroupIdAmbiguous || Number(record.guziWxGroupCount) > 1) {
          return <Tag color="warning">Guzi 多群</Tag>;
        }
        if (Number(record.guziWxGroupCount) === 1) {
          return <Tag color="processing">待群级确认</Tag>;
        }
        return "-";
      },
    },
    {
      title: "外部用户 ID",
      dataIndex: "externalUserId",
      key: "externalUserId",
      width: 280,
      render: (value) => <Text copyable>{value}</Text>,
    },
    {
      title: "昵称",
      dataIndex: "nickname",
      key: "nickname",
      width: 200,
      render: (value, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-flex",
              width: 28,
              height: 28,
              borderRadius: 4,
              overflow: "hidden",
              background: "#e5e7eb",
              flexShrink: 0,
            }}
          >
            {record.avatar ? (
              <img
                src={String(record.avatar)}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                loading="lazy"
              />
            ) : null}
          </span>
          <span>{value || "-"}</span>
        </div>
      ),
    },
    {
      title: "wxid",
      dataIndex: "wxid",
      key: "wxid",
      width: 200,
      render: (value) => (value ? <Text copyable>{value}</Text> : "-"),
    },
    {
      title: "白名单",
      dataIndex: "isWhitelisted",
      key: "isWhitelisted",
      width: 100,
      render: (value) => (
        <Tag color={value ? "green" : "default"}>{value ? "是" : "否"}</Tag>
      ),
    },
    {
      title: "企微 ID",
      dataIndex: "qwId",
      key: "qwId",
      width: 200,
      render: (value) => (value ? <Text copyable>{value}</Text> : "-"),
    },
    {
      title: "淘客 ID",
      dataIndex: "taokeId",
      key: "taokeId",
      width: 200,
      render: (value) => (value ? <Text copyable>{value}</Text> : "-"),
    },
    {
      title: "风险指数",
      dataIndex: "riskIndex",
      key: "riskIndex",
      width: 100,
      render: (value) => (typeof value === "number" ? value : "-"),
    },
    {
      title: "Tenant",
      dataIndex: "tenantId",
      key: "tenantId",
      width: 160,
      render: (value) => <Text copyable>{value}</Text>,
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: formatTime,
    },
    {
      title: "操作",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_value, record) => (
        <Tooltip title="忽略 24 小时同步缓存，立即从产出系统重新拉取该账号">
          <Button
            size="small"
            icon={<SyncOutlined />}
            loading={refreshingId === record.id}
            onClick={() => refreshRow(record)}
          >
            刷新
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <Stack size={20} className="fade-in">
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            发言名单
          </Title>
          <Text type="secondary">
            记录所有出现过发言的外部用户及其白名单状态，首次发言时查询产出系统数据源判定。
          </Text>
        </div>
        <Space>
          <Popconfirm
            title="清空发言名单"
            description="将删除所有发言名单记录，下次发言时会重新判定白名单状态，且不可恢复。"
            okText="清空"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            disabled={total === 0}
            onConfirm={() => {
              trpc.fadan.speakerRoster.clear
                .mutate()
                .then((result) => {
                  message.success(`已清空 ${result.deletedCount} 条记录`);
                  setFilters((prev) => ({ ...prev, page: 1 }));
                  loadData();
                })
                .catch((error: any) => message.error(error?.message || "清空失败"));
            }}
          >
            <Button danger icon={<DeleteOutlined />} disabled={total === 0}>
              清空
            </Button>
          </Popconfirm>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            刷新
          </Button>
        </Space>
      </div>
      <Card variant="borderless" className="shadow-sm">
        <div className="monitor-toolbar">
          <Input.Search
            allowClear
            placeholder="搜索 ID / 昵称"
            style={{ width: 260 }}
            value={filters.keyword}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, keyword: event.target.value, page: 1 }))
            }
            onSearch={loadData}
          />
          <Input.Search
            allowClear
            placeholder="淘客 ID"
            style={{ width: 180 }}
            value={taokeIdInput}
            onChange={(event) => {
              const value = event.target.value;
              setTaokeIdInput(value);
              // 清空时立即恢复全量；输入过程不查询，回车/点搜索才按 ID 过滤
              if (!value.trim()) {
                setFilters((prev) => ({ ...prev, taokeId: "", page: 1 }));
              }
            }}
            onSearch={(value) =>
              setFilters((prev) => ({ ...prev, taokeId: value.trim(), page: 1 }))
            }
          />
          <Select
            allowClear
            placeholder="白名单状态"
            style={{ width: 160 }}
            value={filters.isWhitelisted}
            options={[
              { label: "白名单", value: true },
              { label: "非白名单", value: false },
            ]}
            onChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                isWhitelisted: value,
                page: 1,
              }))
            }
          />
        </div>
      </Card>
      <Card variant="borderless" className="shadow-sm">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total,
            onChange: (page, pageSize) =>
              setFilters((prev) => ({ ...prev, page, limit: pageSize })),
          }}
        />
      </Card>
    </Stack>
  );
}
